import { endOfDay, format, set, startOfDay, subDays } from "date-fns";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dropdown, Icon, Loader, Message } from "semantic-ui-react";

import { DATE_FORMATS_FNS, DatePicker, DetailsCard, NavPortal, Tabs } from "components";
import { useSelectedDealer } from "components/DealersAndLocationsDropdown/hooks";
import { AppointmentSurveyReport, DealerQualityChecksTabular, DealerReportTable, DealerUsersReportTable, TyreMotionReport } from "modules/DealerReports/components";
import { useDealerQualityChecksFilterValues } from "modules/DealerReports/components/DealerQualityChecksTabular/hooks";
import "modules/DealerReports/DealerReports.scss";
import { useDealerReports } from "modules/DealerReports/hooks/useDealerReports";
import { useExportReportOds } from "modules/DealerReports/hooks/useExportReportOds";
import { REPORT_CONFIGS } from "modules/DealerReports/util/config";
import { formatPercent, isPercentageField } from "modules/DealerReports/util/helper";
import { SimpleBarChart } from "modules/LocationReports/components";
import { toISOString } from "utils/dateHelperFunctions";

import type { UseQueryResult } from "@tanstack/react-query";
import type { TabData } from "components";
import type { SurveyReportData } from "modules/DealerReports/components/AppointmentSurveyReport";
import type { QualityCheckReportPayload } from "modules/DealerReports/components/DealerQualityChecksTabular";
import type { TyreMotionData } from "modules/DealerReports/components/TyreMotionReport";
import type { ParsedDealerReport } from "modules/DealerReports/util/parser";
import type { ReportThreshold } from "modules/LocationReports";

enum WO_FILTER {
  All = "All",
  Internal = "Internal",
  Lease = "Lease",
  PrivateCompany = "PrivateCompany"
}

enum REPORT_PERIOD {
  Week = 7,
  Month = 30,
  Quarter = 90,
  Custom = "custom"
}

export enum REPORT_GROUP {
  Appointments = 1,
  Employees,
  Communication,
  Snooze,
  Keylockers,
  QualityCheck,
  Tyres,
  Kiosk,
  TyreMotion
}

type PERIOD = REPORT_PERIOD.Week | REPORT_PERIOD.Month | REPORT_PERIOD.Quarter | REPORT_PERIOD.Custom;

export const REPORT_IDS = {
  QualityCheck: 6,
  UserReportTable: 24,
  TyreMotionReport: 26,
  v7LocationOverview: 10,
  v7LeaseOverview: 12,
  v7LocationOverviewCommercial: 30,
  AppointmentSurvey: 31
};

const WoFilterLabel = new Map<WO_FILTER, { translationKey: string; defaultMessage: string }>([
  [WO_FILTER.All, { translationKey: "v8_all", defaultMessage: "All" }],
  [WO_FILTER.Internal, { translationKey: "v8_internal", defaultMessage: "Internal" }],
  [WO_FILTER.Lease, { translationKey: "v8_lease", defaultMessage: "Lease" }],
  [WO_FILTER.PrivateCompany, { translationKey: "v8_private_company", defaultMessage: "Private / Company" }]
]);

const subtractDays = (days: number): Date => {
  const currentDate = new Date();
  const dateAfterSubtractingDays = subDays(currentDate, days);

  const dateWithTime = set(dateAfterSubtractingDays, { hours: 12, minutes: 0, seconds: 0 });
  return new Date(toISOString(dateWithTime));
};

export const DealerReports = () => {
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [dateFrom, setDateFrom] = useState<Date | null>(subtractDays(7));
  const [dateTo, setDateTo] = useState<Date | null>(subtractDays(1));
  const [expanded, setExpanded] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<PERIOD>(REPORT_PERIOD.Week);
  const [selectedWoFilter, setSelectedWoFilter] = useState<WO_FILTER>(WO_FILTER.All);
  const [isInternal, setIsInternal] = useState<boolean | null>(null);
  const [isLeaseCompany, setIsLeaseCompany] = useState<boolean | null>(null);
  const { t } = useTranslation();

  const { selectedDealer } = useSelectedDealer();

  const { mutate: exportOds, isPending: isExporting, variables: exportPayload } = useExportReportOds();

  const qualityCheckFilterValues = useDealerQualityChecksFilterValues();
  const { mechanicId, locationId, qcOfficerId, qcStatus, qcChecklistId } = qualityCheckFilterValues;

  const { groups, selectedGroup, setSelectedGroup, reports, emailStats, loading } = useDealerReports({
    dealer_id: Number(selectedDealer?.id),
    date_from: dateFrom ? format(dateFrom, DATE_FORMATS_FNS.ISOFormatStartOfDayWithZ) : undefined,
    date_to: dateTo ? format(dateTo, DATE_FORMATS_FNS.ISOFormatEndOfDayWithZ) : undefined,
    custom_domain_enabled: selectedDealer?.custom_domain_enabled,
    is_internal: isInternal,
    is_lease_company: isLeaseCompany,
    qualityCheckFilters: { mechanicId, locationId, qcOfficerId, qcStatus, qcChecklistId }
  });

  const onSelectWoFilter = (value: WO_FILTER) => {
    setSelectedWoFilter(value);

    switch (value) {
      case WO_FILTER.Internal:
        setIsInternal(true);
        setIsLeaseCompany(false);
        break;
      case WO_FILTER.Lease:
        setIsInternal(false);
        setIsLeaseCompany(true);
        break;
      case WO_FILTER.PrivateCompany:
        setIsInternal(false);
        setIsLeaseCompany(false);
        break;
      default:
        setIsInternal(null);
        setIsLeaseCompany(null);
    }
  };

  const woFilterOptions = useMemo(() => {
    return Object.values(WO_FILTER).map(value => {
      const labelInfo = WoFilterLabel.get(value) || { translationKey: "", defaultMessage: value.toString() };
      return {
        key: value,
        value,
        text: t(labelInfo.translationKey) || labelInfo.defaultMessage
      };
    });
  }, [t]);

  const tabsData = new Map<string, TabData>([
    ...reports.map(
      report =>
        [
          `report_${report.data?.id}`,
          {
            id: `report_${report.data?.id}`,
            title: (report.data && t(`v8_${report.data.report_name}`)) || report.data?.report_name,
            data: []
          }
        ] as [string, TabData]
    ),
    ...(selectedGroup === REPORT_GROUP.Communication && selectedDealer?.custom_domain_enabled
      ? [
          [
            "email_stats",
            {
              id: "email_stats",
              title: t("v8_email_stats", "Email Statistics"),
              data: []
            }
          ] as [string, TabData]
        ]
      : [])
  ]);

  const onSelectPeriod = (value: PERIOD) => {
    setSelectedPeriod(value);

    const period = (value === REPORT_PERIOD.Custom ? value : Number(value)) as PERIOD;

    switch (period) {
      case REPORT_PERIOD.Week:
      case REPORT_PERIOD.Month:
      case REPORT_PERIOD.Quarter:
        setDateFrom(subtractDays(period));
        setDateTo(subtractDays(1));
        setShowCustomRange(false);
        break;
      case REPORT_PERIOD.Custom:
        setShowCustomRange(true);
        break;
      default:
        break;
    }
  };

  const toggleExpand = () => {
    setExpanded(prev => !prev);
  };

  const handleDateChange = (...dates: (Date | null)[]) => {
    const [start, end] = dates;
    setDateFrom(start ? startOfDay(start) : null);
    setDateTo(end ? endOfDay(end) : null);
  };

  const periodLabels: Record<PERIOD, string> = {
    [REPORT_PERIOD.Week]: `7 ${t("v8_days", "Days")}`,
    [REPORT_PERIOD.Month]: `30 ${t("v8_days", "Days")}`,
    [REPORT_PERIOD.Quarter]: `90 ${t("v8_days", "Days")}`,
    [REPORT_PERIOD.Custom]: t("v8_custom", "Custom")
  };

  const isReportExporting = (reportName: string) => isExporting && exportPayload.name === reportName;

  const isHiddenOnExport = (value: unknown) => {
    if (typeof value !== "object" || value === null || !("params" in value)) return false;
    const params = value.params;
    return typeof params === "object" && params !== null && "hideOnExport" in params && params.hideOnExport === true;
  };

  const getCountAndPercent = (value: unknown) => {
    if (typeof value !== "object" || value === null || !("value" in value) || !("percent" in value)) return null;
    return value;
  };

  const handleExportData = (name: string, data: Record<string, unknown>[], reportId?: number) => {
    if (!data.length) return;

    const calculations = reportId !== undefined ? REPORT_CONFIGS[reportId].calculations : undefined;
    const exportedFieldKeys = Object.keys(data[0]).filter(key => !isHiddenOnExport(data[0][key]));

    const formatOdsCell = (key: string, value: unknown) => {
      if (value === null || value === undefined) return "";

      if (typeof value === "object" && "value" in value) return value.value;

      if (isPercentageField(key)) return formatPercent(value);

      return value;
    };

    const columnDefs = exportedFieldKeys.flatMap(key => {
      const label = t(calculations?.[key]?.labelKey || `v8_${key}`) || key;

      if (!getCountAndPercent(data[0][key])) return [{ key, header: label, isSplit: false, isPercent: false }];

      return [
        { key, header: `${label} (${t("v8_count", "count")})`, isSplit: true, isPercent: false },
        { key, header: `${label} (%)`, isSplit: true, isPercent: true }
      ];
    });

    const columns = columnDefs.map(column => column.header);

    const rows = data.map(row => {
      const rowByColumn: Record<string, unknown> = {};

      columnDefs.forEach(column => {
        const value = row[column.key];

        if (!column.isSplit) {
          rowByColumn[column.header] = formatOdsCell(column.key, value);
          return;
        }

        const countAndPercent = getCountAndPercent(value);
        rowByColumn[column.header] = column.isPercent ? formatPercent(countAndPercent?.percent) : (countAndPercent?.value ?? "");
      });

      return rowByColumn;
    });

    exportOds({ name, columns, rows });
  };

  const renderReports = (report: UseQueryResult<ParsedDealerReport>, idx: number) => {
    if (report.data?.report_type === "DealerQualityChecksTabular" && report.data.report_data[0])
      return (
        <DealerQualityChecksTabular
          key={`report_${report.data.id}`}
          data={report.data.report_data[0] as unknown as QualityCheckReportPayload}
          reportName={report.data.report_name}
          isExpanded={expanded}
          id={`report_${report.data.id}`}
          handleExportData={handleExportData}
          isExporting={isReportExporting(report.data.report_name)}
          title={tabsData.get(`report_${report.data.id}`)?.title}
          isFetching={report.isFetching}
          {...qualityCheckFilterValues}
        />
      );

    if (report.isLoading) return <Loader active inline key={`dealer-reports-loader-${idx}`} />;

    if (report.isError) return <Message>{t("v8_failed_to_get_report", "Failed to get the report")}</Message>;

    if (!report.data) return null;

    if (!report.data.report_data.length)
      return (
        <DetailsCard
          key={`report_${report.data.id}`}
          title={tabsData.get(`report_${report.data.id}`)?.title}
          sectionMenuId={`report_${report.data.id}`}
          isExpanded={expanded}
        >
          <div className="DealerReports__container-no-data">{t("v8_no_data", "No data")}</div>
        </DetailsCard>
      );

    const thresholds = report.data.thresholds || [];

    if (Array.isArray(thresholds) && thresholds.length > 0) {
      thresholds.forEach((th: ReportThreshold) => {
        if (th.name) th.name = t(th.name) || th.name;
      });
    }

    if (report.data.report_type === "SimpleBarChart")
      return (
        <DetailsCard
          key={`report_${report.data.id}`}
          title={tabsData.get(`report_${report.data.id}`)?.title}
          sectionMenuId={`report_${report.data.id}`}
          isExpanded={expanded}
        >
          <SimpleBarChart
            key={report.data.report_name}
            colors={report.data.report_colors}
            thresholds={Array.isArray(thresholds) ? thresholds : []}
            data={report.data.report_data}
            unit={report.data.unit}
          />
        </DetailsCard>
      );

    if (report.data.report_type === "Tabular") {
      if (report.data.id === REPORT_IDS.AppointmentSurvey)
        return (
          <DetailsCard
            key={`report_${report.data.id}`}
            title={tabsData.get(`report_${report.data.id}`)?.title}
            sectionMenuId={`report_${report.data.id}`}
            isExpanded={expanded}
          >
            <AppointmentSurveyReport data={report.data.report_data[0] as unknown as SurveyReportData} />
          </DetailsCard>
        );

      return (
        <DetailsCard
          key={`report_${report.data.id}`}
          title={tabsData.get(`report_${report.data.id}`)?.title}
          sectionMenuId={`report_${report.data.id}`}
          rightContent={
            <div className="export-button">
              <Button
                icon
                color="green"
                basic
                disabled={isReportExporting(report.data.report_name)}
                loading={isReportExporting(report.data.report_name)}
                onClick={() => handleExportData(report.data.report_name, report.data.report_data, report.data.id)}
              >
                <Icon className="file export" />
              </Button>
            </div>
          }
          isExpanded={expanded}
        >
          {report.data.id === REPORT_IDS.UserReportTable && <DealerUsersReportTable data={report.data.report_data} />}
          {report.data.id === REPORT_IDS.TyreMotionReport && <TyreMotionReport data={report.data.report_data as unknown as TyreMotionData[]} />}

          {![REPORT_IDS.UserReportTable, REPORT_IDS.TyreMotionReport].includes(report.data.id) && <DealerReportTable data={report.data.report_data} />}
        </DetailsCard>
      );
    }
  };

  const renderEmailStats = () => {
    if (selectedGroup !== 3) return;

    if (emailStats.isLoading) return <Loader active inline />;
    if (emailStats.error)
      return (
        <DetailsCard title={tabsData.get(`email_stats`)?.title} sectionMenuId={`email_stats`} isExpanded={expanded}>
          <div className="DealerReports__container-no-data">{t("v8_error_loading_data", "Error loading data")}</div>
        </DetailsCard>
      );

    if (!emailStats.data) return null;

    if (!emailStats.data.length)
      return (
        <DetailsCard title={tabsData.get(`email_stats`)?.title} sectionMenuId={`email_stats`} isExpanded={expanded}>
          <div className="DealerReports__container-no-data">{t("v8_no_data", "No data")}</div>
        </DetailsCard>
      );

    return (
      <DetailsCard title={tabsData.get(`email_stats`)?.title} sectionMenuId={"email_stats"} isExpanded={expanded}>
        <SimpleBarChart key="email_stats" data={emailStats.data} />
      </DetailsCard>
    );
  };

  const showWoFilter = selectedGroup && [REPORT_GROUP.Appointments, REPORT_GROUP.Employees, REPORT_GROUP.Communication].includes(selectedGroup);

  return (
    <>
      <NavPortal>
        <div className="nav-bar-start DealerReports__dropdowns">
          <div className="DealerReports__dropdowns-container">
            {showWoFilter && (
              <>
                <label htmlFor="dealer_report_wo_filter">{t("v8_wo_filter", "WO Filter")}</label>
                <Dropdown
                  id="dealer_report_wo_filter"
                  className="nav-bar-map"
                  button
                  floating
                  labeled
                  selection
                  value={selectedWoFilter}
                  onChange={(_e, { value }) => onSelectWoFilter(value as WO_FILTER)}
                  options={woFilterOptions}
                />
              </>
            )}

            <label htmlFor="dealer_report_group">{t("v8_report", "Report")}</label>
            <Dropdown
              id="dealer_report_group"
              className="nav-bar-map"
              button
              floating
              labeled
              selection
              value={Number(selectedGroup)}
              onChange={(_e, { value }) => setSelectedGroup(Number(value))}
              options={groups.data?.map(i => ({ key: i, value: Number(i), text: t(`v8_dealer_report_group_${i}`) || i })) || []}
            />
            <label htmlFor="dealer_report_period">{t("v8_select_time_period", "Select time period")}</label>
            <Dropdown
              id="dealer_report_period"
              className="nav-bar-map"
              button
              floating
              labeled
              selection
              value={selectedPeriod}
              onChange={(_e, { value }) => onSelectPeriod(value as PERIOD)}
              options={Object.values(REPORT_PERIOD)
                .filter(value => !!periodLabels[value as PERIOD])
                .map(value => ({
                  key: value,
                  text: periodLabels[value as PERIOD],
                  value
                }))}
            />
            {showCustomRange && <DatePicker onDateChange={handleDateChange} selectsRange startDate={dateFrom} endDate={dateTo} maxRange={90} showLabel />}
          </div>
        </div>
      </NavPortal>

      <div className="DealerReports__container">
        <div className="DealerReports__container-header">
          <div className="DealerReports__container-header-wrapper">
            <div className="row">
              <Tabs data={tabsData} hideBorder offset={-190} />
              <Button className="-appointment-status icon" size="small" onClick={toggleExpand}>
                <Icon className={`arrows ${expanded ? "minimize" : "maximize"}`} color="green" />
              </Button>
            </div>
          </div>
        </div>

        <div className="DealerReports__container-content">
          {loading && <Loader active />}
          {reports.map((r, idx) => renderReports(r, idx))}
          {renderEmailStats()}
        </div>
      </div>
    </>
  );
};
